<script lang="ts">
  import { onMount } from 'svelte';
  import { game } from '$stores/game.svelte';
  import { theme } from '$stores/theme.svelte';
  import { pair, MAX_TWINS } from '$stores/pair.svelte';
  import { profile } from '$stores/profile.svelte';
  import { twinsDrawer } from '$stores/twinsDrawer.svelte';
  import {
    listEchoes,
    deleteEcho,
    clearAllEchoes,
    type EchoRecord
  } from '$game/echo';
  import { CATEGORIES } from '$data/categories';
  import AudioToggle from './AudioToggle.svelte';

  /** Tabs use stable string ids so deep-linking / persistence is trivial. */
  type TabId = 'appearance' | 'twins' | 'history' | 'reset' | 'about';

  interface TabDef {
    id: TabId;
    label: string;
    /** Inline SVG path data for the icon (single 24x24 stroke icon). */
    icon: string;
  }

  const TABS: TabDef[] = [
    {
      id: 'appearance',
      label: 'Theme',
      icon:
        '<circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />'
    },
    {
      id: 'twins',
      label: 'Twins',
      icon:
        '<circle cx="9" cy="8" r="3.5" /><circle cx="16" cy="9" r="2.5" /><path d="M3 20c0-3 2.5-5 6-5s6 2 6 5" /><path d="M14 20c.4-2 2-3.5 4.5-3.5" />'
    },
    {
      id: 'history',
      label: 'History',
      icon:
        '<path d="M3 12a9 9 0 1 0 3-6.7" /><polyline points="3 4 3 9 8 9" /><path d="M12 7v5l3 2" />'
    },
    {
      id: 'reset',
      label: 'Reset',
      icon:
        '<path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />'
    },
    {
      id: 'about',
      label: 'About',
      icon:
        '<circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />'
    }
  ];

  /** Active tab — Appearance is the safest default landing spot. */
  let activeTab = $state<TabId>('appearance');

  /**
   * Map element id → short display label across every category. Used to
   * render past-play entries compactly without re-walking the whole
   * categories tree on each row. Built once on mount; categories are
   * static so a single pass is enough.
   */
  let elementLabels = $state<Record<string, string>>({});

  /** Raw echo records pulled from localStorage. Re-read whenever the
   *  Settings screen mounts and after destructive clear actions. */
  let echoes = $state<EchoRecord[]>([]);

  /** Two-tap confirmation state for destructive actions. Maps action key
   *  → timeout id; presence means "tap again to confirm". */
  let armed = $state<Record<string, ReturnType<typeof setTimeout>>>({});

  let toast = $state('');
  let toastVisible = $state(false);

  /** Bumps every minute so "X minutes ago" stays fresh while the screen
   *  is open. We don't run this on a global timer because Settings is
   *  unmounted whenever the user navigates away. */
  let nowMs = $state(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;

  onMount(() => {
    profile.hydrate();
    pair.hydrate();
    elementLabels = buildElementLabels();
    echoes = listEchoes();
    timer = setInterval(() => (nowMs = Date.now()), 60_000);
    return () => {
      if (timer) clearInterval(timer);
    };
  });

  function buildElementLabels(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const cat of CATEGORIES) {
      for (const el of cat.elements) {
        out[el.id] = el.short || el.label || el.name || el.id;
      }
    }
    return out;
  }

  function flash(msg: string) {
    toast = msg;
    toastVisible = true;
    setTimeout(() => (toastVisible = false), 1700);
  }

  function back() {
    game.goto(game.scoreboardOrigin || 'intro');
  }

  function pickTab(id: TabId) {
    activeTab = id;
  }

  function pickTheme(t: 'modern' | 'brutal') {
    theme.set(t);
  }

  function openTwins() {
    twinsDrawer.openDrawer();
  }

  /** Two-tap confirm: first tap "arms" the action for 3s, second tap fires it. */
  function arm(key: string, run: () => void) {
    const existing = armed[key];
    if (existing) {
      clearTimeout(existing);
      const next = { ...armed };
      delete next[key];
      armed = next;
      run();
      return;
    }
    const id = setTimeout(() => {
      const next = { ...armed };
      delete next[key];
      armed = next;
    }, 3000);
    armed = { ...armed, [key]: id };
  }

  function clearPastPlays() {
    arm('past-plays', () => {
      clearAllEchoes();
      echoes = listEchoes();
      flash('Past plays cleared');
    });
  }

  function deleteOneEcho(sig: string) {
    deleteEcho(sig);
    echoes = listEchoes();
  }

  function resetProfile() {
    arm('profile', () => {
      profile.reset();
      profile.hydrate();
      flash('Profile reset');
    });
  }

  function unbondAllTwins() {
    arm('twins', () => {
      pair.unbondAll();
      flash('All twins unbonded');
    });
  }

  function resetEverything() {
    arm('all', () => {
      clearAllEchoes();
      profile.reset();
      profile.hydrate();
      pair.unbondAll();
      echoes = listEchoes();
      game.reset();
      // Replay the splash via the layout listener.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('vutwin:replay'));
      }
      flash('Game reset');
    });
  }

  function isArmed(key: string): boolean {
    return Boolean(armed[key]);
  }

  function fmtRelative(ts: number): string {
    const diffMin = Math.max(0, Math.round((nowMs - ts) / 60_000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const h = Math.round(diffMin / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.round(d / 7);
    return `${w}w ago`;
  }

  function labelFor(id: string): string {
    return elementLabels[id] || id;
  }
</script>

<section class="st">
  <div class="st-head">
    <button class="st-back icon-btn" type="button" aria-label="Back" onclick={back}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <div class="st-title">SETTINGS</div>
    <span class="st-spacer"></span>
  </div>

  <div class="st-tabs" role="tablist" aria-label="Settings sections">
    {#each TABS as tab (tab.id)}
      <button
        type="button"
        role="tab"
        id="st-tab-{tab.id}"
        aria-controls="st-panel-{tab.id}"
        aria-selected={activeTab === tab.id}
        class="st-tab"
        class:active={activeTab === tab.id}
        onclick={() => pickTab(tab.id)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          {@html tab.icon}
        </svg>
        <span class="st-tab-label">{tab.label}</span>
      </button>
    {/each}
  </div>

  <div class="st-body">
    {#if activeTab === 'appearance'}
      <div
        class="st-card"
        role="tabpanel"
        id="st-panel-appearance"
        aria-labelledby="st-tab-appearance"
      >
        <div class="st-card-label">APPEARANCE</div>
        <div class="st-card-sub">
          Switch between the saturated arcade theme and the brutalist mono theme.
        </div>
        <div class="st-segmented" role="radiogroup" aria-label="Theme">
          <button
            type="button"
            role="radio"
            class="st-seg"
            class:active={theme.current === 'modern'}
            aria-checked={theme.current === 'modern'}
            onclick={() => pickTheme('modern')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
            Modern
          </button>
          <button
            type="button"
            role="radio"
            class="st-seg"
            class:active={theme.current === 'brutal'}
            aria-checked={theme.current === 'brutal'}
            onclick={() => pickTheme('brutal')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="4" y="4" width="16" height="16" />
            </svg>
            Brutalist
          </button>
        </div>

        <div class="st-divider" aria-hidden="true"></div>

        <div class="st-card-label">SOUND</div>
        <div class="st-card-sub">
          A short, fully-synthesized chime plays when you bond with a twin. Tap Preview to hear it.
        </div>
        <AudioToggle />
      </div>
    {:else if activeTab === 'twins'}
      <div
        class="st-card"
        role="tabpanel"
        id="st-panel-twins"
        aria-labelledby="st-tab-twins"
      >
        <div class="st-card-label">TWINS</div>
        <div class="st-card-sub">
          {#if pair.active.length === 0}
            You have no bonded twins yet. Open the picker to share your bond code.
          {:else}
            {pair.active.length} of {MAX_TWINS} twins bonded
            {#if pair.isBonded}· active <strong>@{pair.friendHandle}</strong>{/if}
          {/if}
        </div>
        <div class="st-row-actions">
          <button type="button" class="btn btn-secondary" onclick={openTwins}>Manage twins</button>
        </div>
      </div>
    {:else if activeTab === 'history'}
      <div
        class="st-card"
        role="tabpanel"
        id="st-panel-history"
        aria-labelledby="st-tab-history"
      >
        <div class="st-card-label">PAST PLAYS</div>
        <div class="st-card-sub">
          Your latest ranking for each prompt you've played, kept locally for 30 days. Never leaves this device.
        </div>
        {#if echoes.length === 0}
          <div class="st-empty">No past plays yet — finish a Pulse to see your echoes here.</div>
        {:else}
          <ul class="st-echo-list">
            {#each echoes as e (e.sig)}
              <li class="st-echo-row">
                <div class="st-echo-meta">
                  <span class="st-echo-time">{fmtRelative(e.ts)}</span>
                  <span class="st-echo-sig">#{e.sig}</span>
                </div>
                <div class="st-echo-order" aria-label="Your ranking, left to right">
                  {#each e.order as id, i (id + i)}
                    <span class="st-echo-tile">
                      <span class="st-echo-rank">{i + 1}</span>
                      <span class="st-echo-label">{labelFor(id)}</span>
                    </span>
                  {/each}
                </div>
                <button
                  type="button"
                  class="st-echo-del"
                  aria-label="Delete past play"
                  onclick={() => deleteOneEcho(e.sig)}
                >×</button>
              </li>
            {/each}
          </ul>
          <button
            type="button"
            class="st-danger"
            class:armed={isArmed('past-plays')}
            onclick={clearPastPlays}
          >
            {isArmed('past-plays') ? 'Tap again to confirm' : 'Clear all past plays'}
          </button>
        {/if}
      </div>
    {:else if activeTab === 'reset'}
      <div
        class="st-card danger-card"
        role="tabpanel"
        id="st-panel-reset"
        aria-labelledby="st-tab-reset"
      >
        <div class="st-card-label">RESET</div>
        <div class="st-card-sub">
          Destructive actions. Tap once to arm, again within 3 seconds to confirm.
        </div>
        <div class="st-reset-stack">
          <button
            type="button"
            class="st-danger"
            class:armed={isArmed('profile')}
            onclick={resetProfile}
          >
            {isArmed('profile') ? 'Tap again — wipe handle, streak, history' : 'Reset profile'}
          </button>
          <button
            type="button"
            class="st-danger"
            class:armed={isArmed('twins')}
            onclick={unbondAllTwins}
          >
            {isArmed('twins') ? 'Tap again — unbond every twin' : 'Unbond all twins'}
          </button>
          <button
            type="button"
            class="st-danger st-danger-strong"
            class:armed={isArmed('all')}
            onclick={resetEverything}
          >
            {isArmed('all') ? 'Tap again — wipe EVERYTHING' : 'Reset everything'}
          </button>
        </div>
      </div>
    {:else if activeTab === 'about'}
      <div
        class="st-card"
        role="tabpanel"
        id="st-panel-about"
        aria-labelledby="st-tab-about"
      >
        <div class="st-card-label">ABOUT</div>
        <div class="st-card-sub">
          VuTwin · A 60-second daily pulse with your closest friend. Server-blind by design — your bonds, rankings, and past plays live on this device. Bonds last 30 days from the most recent shared pulse and break the moment either side unbonds.
        </div>
      </div>
    {/if}
  </div>

  {#if toastVisible}
    <div class="st-toast" role="status" aria-live="polite">{toast}</div>
  {/if}
</section>

<style>
  .st {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }
  .st-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px var(--gutter);
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  :global(body.brutal) .st-head {
    border-bottom-width: 2px;
  }
  .icon-btn {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    background: var(--surface);
    border: 1px solid var(--border);
    display: grid;
    place-items: center;
  }
  :global(body.brutal) .icon-btn {
    border-width: 2px;
  }
  .icon-btn svg {
    width: 14px;
    height: 14px;
  }
  .st-title {
    flex: 1;
    font-weight: 800;
    letter-spacing: 0.1em;
    font-size: 12px;
    text-transform: uppercase;
  }
  .st-spacer {
    width: 32px;
  }

  /* Tabs strip — horizontal scroll on narrow viewports, equal-share on wide. */
  .st-tabs {
    flex: 0 0 auto;
    display: flex;
    gap: 4px;
    padding: 8px var(--gutter);
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }
  .st-tabs::-webkit-scrollbar {
    display: none;
  }
  :global(body.brutal) .st-tabs {
    border-bottom-width: 2px;
  }
  .st-tab {
    flex: 1 1 0;
    min-width: 64px;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 6px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    color: var(--text-3);
    font-family: inherit;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    transition: var(--transition);
    position: relative;
    white-space: nowrap;
  }
  .st-tab:hover {
    color: var(--text-2);
    background: var(--surface);
  }
  .st-tab.active {
    color: var(--text);
    background: var(--surface);
    border-color: var(--border);
  }
  .st-tab.active::after {
    content: '';
    position: absolute;
    left: 14%;
    right: 14%;
    bottom: -9px;
    height: 2px;
    background: var(--cyan);
    border-radius: 2px;
  }
  :global(body.brutal) .st-tab {
    border-radius: 0;
  }
  :global(body.brutal) .st-tab.active {
    color: var(--text);
    background: var(--text);
    color: var(--bg);
    border-color: var(--text);
  }
  :global(body.brutal) .st-tab.active::after {
    background: var(--text);
  }
  .st-tab svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }
  .st-tab-label {
    font-size: 10px;
    line-height: 1;
  }

  .st-body {
    flex: 1;
    overflow-y: auto;
    padding: 14px var(--gutter) max(var(--safe-cta-bottom), 16px);
    display: flex;
    flex-direction: column;
    gap: 12px;
    -webkit-overflow-scrolling: touch;
  }

  .st-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: st-tab-in 220ms var(--spring-press) both;
  }
  :global(body.brutal) .st-card {
    border-width: 2px;
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .danger-card {
    border-color: var(--border-warm);
  }
  :global(body.brutal) .danger-card {
    border-color: var(--text);
  }
  @media (prefers-reduced-motion: reduce) {
    .st-card {
      animation: none;
    }
  }
  @keyframes st-tab-in {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
  }

  .st-card-label {
    font-size: 10px;
    color: var(--text-3);
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 800;
  }
  .st-card-sub {
    font-size: 12px;
    color: var(--text-2);
    line-height: 1.5;
  }
  .st-card-sub strong {
    color: var(--text);
    font-weight: 800;
  }
  .st-row-actions {
    display: flex;
    gap: 8px;
    margin-top: 2px;
  }
  .st-row-actions .btn {
    flex: 1;
    text-align: center;
  }

  /* Segmented theme switcher. */
  .st-divider {
    height: 1px;
    background: var(--border);
    margin: 6px 0;
  }
  :global(body.brutal) .st-divider {
    height: 2px;
    background: var(--text);
  }
  .st-segmented {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 4px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }
  :global(body.brutal) .st-segmented {
    border-width: 2px;
  }
  .st-seg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 12px;
    background: transparent;
    border-radius: var(--radius-sm);
    color: var(--text-2);
    font-family: inherit;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    transition: var(--transition);
  }
  .st-seg svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
  }
  .st-seg.active {
    background: var(--surface);
    color: var(--text);
    box-shadow: 0 0 0 1px var(--cyan);
  }
  :global(body.brutal) .st-seg.active {
    background: var(--text);
    color: var(--bg);
    box-shadow: none;
  }

  /* Past plays. */
  .st-empty {
    font-size: 12px;
    color: var(--text-3);
    text-align: center;
    padding: 18px 8px;
    background: var(--bg-elev);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
  }
  :global(body.brutal) .st-empty {
    border-style: solid;
    border-width: 2px;
  }
  .st-echo-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .st-echo-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;
    padding: 10px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }
  :global(body.brutal) .st-echo-row {
    border-width: 2px;
  }
  .st-echo-meta {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .st-echo-time {
    font-size: 11px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .st-echo-sig {
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', monospace;
    font-size: 10px;
    color: var(--text-3);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .st-echo-del {
    grid-row: 1 / 3;
    grid-column: 2;
    width: 30px;
    height: 30px;
    border-radius: var(--radius-sm);
    color: var(--text-3);
    font-size: 18px;
    line-height: 1;
    background: transparent;
    transition: var(--transition);
    align-self: center;
  }
  .st-echo-del:hover {
    background: var(--surface-2);
    color: var(--accent-pink);
  }
  .st-echo-order {
    grid-column: 1 / 2;
    grid-row: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    overflow: hidden;
  }
  .st-echo-tile {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 11px;
    font-weight: 700;
    color: var(--text);
  }
  :global(body.brutal) .st-echo-tile {
    border-width: 2px;
  }
  .st-echo-rank {
    font-family: ui-monospace, 'SF Mono', 'JetBrains Mono', monospace;
    font-size: 9px;
    color: var(--text-3);
  }
  .st-echo-label {
    letter-spacing: 0.02em;
  }

  /* Reset / destructive buttons. */
  .st-reset-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .st-danger {
    width: 100%;
    padding: 12px 14px;
    background: var(--bg-elev);
    color: var(--text-2);
    border: 1px solid var(--border-warm);
    border-radius: var(--radius-sm);
    font-family: inherit;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: var(--transition);
  }
  :global(body.brutal) .st-danger {
    border-width: 2px;
    border-color: var(--text);
  }
  .st-danger:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  .st-danger.armed {
    background: rgba(255, 56, 188, 0.15);
    color: var(--text);
    border-color: var(--accent-pink);
  }
  :global(body.brutal) .st-danger.armed {
    background: var(--text);
    color: var(--bg);
  }
  .st-danger-strong {
    background: rgba(255, 56, 188, 0.08);
    color: var(--accent-pink);
    border-color: var(--accent-pink);
  }
  :global(body.brutal) .st-danger-strong {
    background: var(--bg);
    color: var(--text);
    border-color: var(--text);
  }
  .st-danger-strong.armed {
    background: var(--accent-pink);
    color: var(--text-on-light);
  }
  :global(body.brutal) .st-danger-strong.armed {
    background: var(--text);
    color: var(--bg);
  }

  .st-toast {
    position: absolute;
    top: 56px;
    left: 50%;
    transform: translateX(-50%);
    padding: 10px 16px;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 12px;
    font-weight: 600;
    box-shadow: var(--shadow-soft);
    z-index: 50;
    pointer-events: none;
  }
  :global(body.brutal) .st-toast {
    border-width: 2px;
  }
</style>
